import React from 'react';
import { useIntl } from 'react-intl';
import { WithStylesProps } from 'react-jss';

import { black5 } from '~/assets/shared-styles/general';
import { EM_DASH } from '~/common/constants/unicode';
import EcMultipleSelectValue from '~/components/Shared/EcMultipleSelectValue/EcMultipleSelectValue';
import { showToast } from '~/components/Shared/EcToast/showToast';
import SubArrowIcon from '~/components/Shared/Icons/SubArrowIcon';
import * as toastTypes from '~/types/toast.types';
import FileInput from '~/ui/components/FileInput/FileInput';
import FileLabel from '~/ui/components/FileLabel/FileLabel';

import styles from '../InformationField.styles';
import { Field } from '../InformationFieldV2';

interface AttachmentFieldProps extends WithStylesProps<typeof styles> {
  field: Field;
  editMode: boolean;
  fieldValue: any;
  setFieldValue: (value: any) => void;
  onUploadAttachments: (files: any[]) => Promise<any>;
  onDownloadFileAttachment: (fileObject: any) => void;
  onHandleFileAttachError: () => void;
}

interface FileData {
  id: string | number;
  fileName: string;
  fileType: string;
}

export const AttachmentField = ({
  classes,
  field,
  editMode,
  fieldValue,
  setFieldValue,
  onUploadAttachments,
  onDownloadFileAttachment,
  onHandleFileAttachError,
}: AttachmentFieldProps) => {
  const intl = useIntl();

  const handleFileDataChangeHelper = (newFiles: FileData[]) => {
    if (!newFiles || !newFiles.length) {
      showToast(
        toastTypes.ERROR,
        intl.formatMessage({
          id: 'InformationField.couldNotAttachFileMessage',
          defaultMessage: 'Could not attach file.',
          description: 'Error message when file could not be attached',
        }),
      );
      return;
    }
    const newItems = newFiles.map((fileData: FileData) => ({
      display_value: fileData.fileName + fileData.fileType,
      value: fileData.id,
    }));
    setFieldValue([...(fieldValue || []), ...newItems]);
    showToast(
      toastTypes.SUCCESS,
      intl.formatMessage({
        id: 'InformationField.attachFileSuccessMessage',
        defaultMessage: 'Attach file succeeded.',
        description: 'Success message when file is attached',
      }),
    );
  };

  const renderMultipleValues = ({ is_editable }: { is_editable?: boolean }) => {
    return (
      <div className={classes.multipleValuesContainer}>
        <SubArrowIcon color={black5} />
        <div className={classes.multipleValues}>
          {fieldValue?.length ? (
            fieldValue.map((value: any, index: number) => (
              <EcMultipleSelectValue
                label={value.display_value}
                key={index}
                closeHandler={() => {
                  const newState = fieldValue.filter(
                    (item: any) => item.value !== value.value,
                  );
                  setFieldValue(newState);
                }}
                isDisabled={!is_editable}
              />
            ))
          ) : (
            <span className={classes.multipleValues}>
              {intl.formatMessage({
                id: 'InformationField.noValuesSelectedMessage',
                defaultMessage: 'No values selected...',
                description:
                  'Message when no values are selected for multiple select field',
              })}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (editMode) {
    return (
      <div className={classes.editItemContainer}>
        <FileInput
          id={`file-input-${field.id}`}
          label="Upload files"
          file={null}
          acceptedFiles="*"
          enableUpload
          enableDropzone
          enableMultiple
          layout="condensed"
          onChange={async (files) => {
            try {
              const result = await onUploadAttachments(files);
              handleFileDataChangeHelper(result);
            } catch {
              onHandleFileAttachError();
            }
          }}
        />
        {renderMultipleValues(field)}
      </div>
    );
  }

  if (fieldValue?.length) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        {fieldValue.map((fileObject: any) => {
          const file = new File([], fileObject.display_value);
          return (
            <button
              onClick={() => onDownloadFileAttachment(fileObject)}
              key={fileObject.value}
              style={{ cursor: 'pointer' }}
            >
              <FileLabel shouldTruncate file={file} />
            </button>
          );
        })}
      </div>
    );
  } else {
    return EM_DASH;
  }
};
